/**
 * Chat System Constants
 *
 * Centralized configuration for the chat system.
 * All magic numbers and configuration values are defined here.
 */

/**
 * Pagination & Virtual Scrolling
 */
export const CHAT_PAGINATION = {
  /** Number of messages to fetch per page */
  MESSAGES_PER_PAGE: 20,

  /** Starting index for virtual list (high number to allow prepending) */
  KEY_FIRST_ITEM_INDEX: 99999,

  /** Maximum number of messages to cache in sessionStorage */
  MAX_CACHED_MESSAGES: 20
} as const;

/**
 * Message Retry Configuration
 */
export const MESSAGE_RETRY = {
  /** Maximum number of retry attempts before moving to dead letter queue */
  MAX_RETRIES: 3,

  /** Base delay for exponential backoff (milliseconds) */
  BASE_DELAY_MS: 1000,

  /** Maximum delay for exponential backoff (milliseconds) */
  MAX_DELAY_MS: 30000,

  /** Maximum age of a message before it expires (24 hours in milliseconds) */
  MAX_MESSAGE_AGE_MS: 24 * 60 * 60 * 1000,

  /** Timeout for HTTP send operations (milliseconds) */
  SEND_TIMEOUT_MS: 30000
} as const;

/**
 * Dead Letter Queue Configuration
 */
export const DEAD_LETTER_QUEUE = {
  /** Maximum number of failed messages to keep in dead letter queue */
  MAX_MESSAGES: 50,

  /** SessionStorage key for dead letter queue */
  STORAGE_KEY: "messages-dead-letter"
} as const;

/**
 * Polling Configuration
 */
export const POLLING = {
  /** Default polling interval (milliseconds) */
  DEFAULT_INTERVAL_MS: 12000,

  /** Maximum number of poll attempts */
  MAX_POLL_COUNT: 10
} as const;

/**
 * SessionStorage Keys
 */
export const STORAGE_KEYS = {
  /** Key for cached messages */
  MESSAGES: "messages",

  /** Key for local (unsent) messages */
  LOCAL_MESSAGES: "messages-local",

  /** Key for dead letter queue */
  DEAD_LETTER: "messages-dead-letter"
} as const;

/**
 * Timeouts & Delays
 */
export const TIMEOUTS = {
  /** Delay before scrolling to message (milliseconds) */
  SCROLL_DELAY_MS: 30,

  /** Delay for processing queue operations (milliseconds) */
  QUEUE_PROCESS_DELAY_MS: 100,

  /** Timeout for HTTP fallback cleanup (milliseconds) */
  HTTP_FALLBACK_CLEANUP_MS: 100,

  /** Auto-dismiss error timeout for non-blocking errors (milliseconds) */
  ERROR_AUTO_DISMISS_MS: 5000
} as const;

/**
 * Virtual Scroll Configuration
 */
export const VIRTUAL_SCROLL = {
  /** Buffer size for virtual list scrolling */
  SCROLL_BUFFER: 100
} as const;
